import {
  useCallback,
  useMemo,
  useState,
  MouseEvent,
  useEffect,
  useRef,
} from 'react';
import { useForm } from 'react-hook-form';
import { ChannelType, SeqConsultRequest } from '@remodeling/remodeling-manager';
import { SeqForm, Step } from '@/types/seq-consult';
import { seqConsultGateway } from '@/di-container/remodeling-manager/seq-consult/gateway/client';
import { useRouter } from 'next/router';
import _ from 'lodash';
import { useModal } from '@/providers/ModalProvider/ModalProvider';
import { quickConsultGateway } from '@/di-container/remodeling-manager/quick-consult/gateway/client';
import useSWR from 'swr';
import useAuthenticated from '@/hooks/useAuthenticated';
import { RadioType } from './useSeqSearchAddressForm';
import { getAsset } from '@/utils';
import { SEQ_CONSULT_DATA, MATCH_CONSULT_DATA } from '@/constants/seq-consult';
import { useDataLayer } from '@/hooks/useDataLayer';
import {
  GTM_CSTC,
  GTM_CST_KEYWORD,
  GTM_CTA_KEY_NAME,
  GTM_CTA_REMO_EVENT_NAME,
  GTM_MATCH_CONSULT_KEYNAME,
  GTM_MATCH_CONSULT_KEYWORD,
  GTM_SEQ_CONSULT_KEYNAME,
  GTM_SEQ_CONSULT_KEYWORD,
} from '@/constants/gtm';
import { useBraze } from '@/providers/BrazeProvider';
import { format } from 'date-fns';
import { expertGateway } from '@/di-container/expert/detail/gateway/client';

const CONSULT_DATA_MAP = {
  SEQ: SEQ_CONSULT_DATA,
  MATCH: MATCH_CONSULT_DATA,
} as const;

type ConsultDataType = typeof CONSULT_DATA_MAP;
type ConsultTypeKey = keyof ConsultDataType;

const useSeqConsult = () => {
  const hookForm = useForm<SeqForm>({ mode: 'onSubmit' });
  const router = useRouter();
  const consultType = ((): ConsultTypeKey => {
    const type = router.query.consultType;
    return type === 'MATCH' ? 'MATCH' : 'SEQ';
  })();
  const channelType = router.query.channelType as ChannelType;
  const channelId = router.query.channelId;
  const channelUrl = router.query.channelUrl as string;
  const agentEmployeeNo = router.query.agentEmployeeNo as string;
  const { isAuthenticated } = useAuthenticated();
  const { handleOpen } = useModal();
  const [isOpenWorkableBottomSheet, setIsOpenWorkableBottomSheet] =
    useState(false);
  const isApiCall = useRef<boolean>(false);

  const consultData = CONSULT_DATA_MAP[consultType];

  const initialStep = Object.keys(consultData)[0] as Step;
  const [step, setStep] = useState<Step>(initialStep);

  const totalStepLength = useMemo(() => {
    return Object.keys(consultData).length;
  }, [consultData]);

  const currentStepIndex = useMemo(() => {
    const titleKeys = Object.keys(consultData);
    return titleKeys.findIndex((_step) => _step === step);
  }, [step, consultData]);

  const handleChangeStep = useCallback((nextStep: Step) => {
    setStep(nextStep);
  }, []);

  const checkValidity = useCallback(async () => {
    if (step === 'baseConstructionPlan.scopes') {
      return await hookForm.trigger('baseConstructionPlan.etcComment');
    }

    if (step === 'personal') {
      const result = await Promise.all([
        hookForm.trigger('name'),
        hookForm.trigger('tel'),
        hookForm.trigger('site.addressDetail'),
      ]);

      const isPassed = !result.includes(false);

      return isPassed;
    }

    return true;
  }, [step, hookForm]);

  const { createSeqConsult } = seqConsultGateway();
  const { handlePushAndRemove } = useDataLayer();
  const { logCustomEvent } = useBraze();

  // 상담 가능 지역 데이터를 가져오는 함수
  const getWorkAvailableAreasFetcher = useCallback(async () => {
    const apiResponse = await expertGateway().getExpertDetail.exec({
      agentCode: agentEmployeeNo as string,
    });
    return {
      workAvailableAreas: apiResponse.data?.agentDetail.workAvailableAreas,
      agentName: apiResponse.data?.agentDetail.name,
      agencyName: apiResponse.data?.agency.name,
      agentImageUrl: apiResponse.data?.agentDetail.image?.url,
      agentDuty: apiResponse.data?.agentDetail.duty,
    };
  }, [agentEmployeeNo]);

  // 전문가 상담일때만 SWR을 사용하여 데이터 가져오기
  const { data: expertData } = useSWR(
    channelType === 'OP23_01' ? 'getWorkAvailableAreas' : null,
    getWorkAvailableAreasFetcher,
  );

  const handleSubmit = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (isApiCall.current) {
        return;
      }

      isApiCall.current = true;

      const data: SeqConsultRequest = hookForm.getValues();
      const isMatchType = consultType === 'MATCH';

      const response = await createSeqConsult.exec({
        ...data,
        name: data.name.trim(),
        agreement: {
          privacy: data.agreement.privacy ? 'Y' : 'N',
          provide: data.agreement.provide ? 'Y' : 'N',
        },
        baseConstructionPlan: {
          ...data.baseConstructionPlan,
        },
        channel: {
          channelType,
          channelId: _.toNumber(channelId),
          channelUrl,
          agentEmployeeNo,
          body: expertData?.agencyName,
          thumbUrl: expertData?.agentImageUrl,
          ...(expertData?.agentName &&
            expertData?.agentDuty && {
              title: `${expertData.agentName} ${expertData.agentDuty}`,
            }),
        },
        simpleUserAgent: 'PC_WEB',
        consultType,
      });

      if (response?.code === 200) {
        handlePushAndRemove({
          data: {
            event: GTM_CTA_REMO_EVENT_NAME,
            [GTM_CTA_KEY_NAME]: GTM_CSTC,
            gtmKeyword: isMatchType
              ? GTM_MATCH_CONSULT_KEYNAME
              : GTM_SEQ_CONSULT_KEYNAME,
            consult_request_id: response?.data?.id,
            [GTM_CST_KEYWORD]: isMatchType
              ? GTM_MATCH_CONSULT_KEYWORD
              : GTM_SEQ_CONSULT_KEYWORD,
          },
          key: GTM_CTA_KEY_NAME,
        });
        logCustomEvent({
          eventName: 'bz_complete_fastconsult',
          eventProperties: {
            bz_date: format(new Date(), 'yyyy-MM-dd'),
            bz_consultType: 'sequential',
            bz_type: data.site.buildingType,
            bz_area: data.site.houseNMPG,
            bz_state: data.baseConstructionPlan.spaceStatus,
            bz_space: data.baseConstructionPlan.scopes?.join(','),
            bz_duedate: data.baseConstructionPlan.desiredDay,
            bz_postcode: data.site.address.zoneCode,
          },
        });

        const sidoCode = data?.site?.address?.sidoCode;
        const sigunguCode = data?.site?.address?.sigunguCode;

        const gunguCode = _.startsWith(sigunguCode, sidoCode)
          ? _.replace(sigunguCode, sidoCode, '')
          : sigunguCode;

        router.push(
          `/seq-consult/finish?id=${response.data?.id}&sidoCode=${sidoCode}&sigunguCode=${gunguCode}&sidoName=${data.site.address.sidoName}&sigunguName=${data.site.address.sigunguName}`,
        );
      } else if (response?.code === 14199) {
        handleOpen({
          modalProps: {
            text: '고객님의 연락처로 오늘 자\n상담신청 내역이 이미 존재합니다.',
            subtext: '동일한 연락처로 1일 1회 상담신청이 가능합니다.',
            subTextAlign: 'center',
          },
        });

        isApiCall.current = false;
      } else {
        handleOpen({
          modalProps: {
            text: response.message,
            subTextAlign: 'center',
          },
        });

        isApiCall.current = false;
      }
    },
    [
      channelId,
      channelType,
      channelUrl,
      createSeqConsult,
      handleOpen,
      handlePushAndRemove,
      hookForm,
      logCustomEvent,
      router,
      agentEmployeeNo,
      expertData,
      consultType,
    ],
  );

  const handleNext = useCallback(
    async (e?: MouseEvent<HTMLButtonElement>) => {
      e?.preventDefault();
      const titleKeys = Object.keys(consultData);
      const nextStep = titleKeys[currentStepIndex + 1] as Step;

      const validationPassed = await checkValidity();
      if (!validationPassed) {
        return;
      }
      const isLastStep = step === 'personal';
      if (isLastStep && e) {
        handleSubmit(e);
      }

      if (nextStep) {
        handleChangeStep(nextStep);
      }
    },
    [
      currentStepIndex,
      handleChangeStep,
      checkValidity,
      step,
      handleSubmit,
      consultData,
    ],
  );

  const handlePrev = useCallback(
    (e?: MouseEvent<HTMLButtonElement>) => {
      e?.preventDefault();
      const titleKeys = Object.keys(consultData);
      const prevStep = titleKeys[currentStepIndex - 1] as Step;

      if (prevStep) {
        handleChangeStep(prevStep);
      }
    },
    [currentStepIndex, handleChangeStep, consultData],
  );

  const { getBasic } = quickConsultGateway();
  const { data: userBasicInfoResponse } = useSWR(
    isAuthenticated ? 'user-basic-info' : null,
    () => getBasic.exec(),
  );

  const userBasicInfo = useMemo(
    () => userBasicInfoResponse?.data?.user,
    [userBasicInfoResponse?.data?.user],
  );

  const [defaultRadio, setIsDefaultRadio] = useState<RadioType>('default');

  const handleChangeIsDefaultAddress = useCallback(
    (defaultRadio: RadioType) => {
      setIsDefaultRadio(defaultRadio);
    },
    [],
  );

  const stepBannerSrc = useMemo(() => {
    if (
      step === 'baseConstructionPlan.age' ||
      step === 'site.buildingType' ||
      step === 'site.houseNMPG' ||
      step === 'baseConstructionPlan.spaceStatus' ||
      step === 'baseConstructionPlan.consultPurpose' ||
      step === 'baseConstructionPlan.consultExperience' ||
      step === 'baseConstructionPlan.opinionReflect' ||
      step === 'baseConstructionPlan.communicationStyle'
    ) {
      return getAsset('/assets/images/seq-consult/seq_consult_banner_1.jpg');
    } else {
      return getAsset('/assets/images/seq-consult/seq_consult_banner_2.jpg');
    }
  }, [step]);

  const [isTelCertificated, setIsTelCertificated] = useState(false);

  const handleChangeTelCertificated = useCallback(
    (nextCertificated: boolean) => {
      setIsTelCertificated(nextCertificated);
    },
    [],
  );

  useEffect(() => {
    if (userBasicInfoResponse?.data?.user) {
      const address = userBasicInfoResponse?.data?.user?.site?.address;
      const addressDetail =
        userBasicInfoResponse?.data?.user?.site?.addressDetail;
      const name = userBasicInfoResponse?.data?.user?.name;
      const tel = userBasicInfoResponse?.data?.user?.tel;
      hookForm.setValue('site.address', address);
      hookForm.setValue('site.addressDetail', addressDetail);
      hookForm.setValue('name', name);
      hookForm.setValue('tel', tel);
    }
  }, [hookForm, userBasicInfoResponse?.data?.user]);

  useEffect(() => {
    const alertRouting = () => {
      const { site } = hookForm.getValues() ?? {};

      if (step === 'site.buildingType' && !site?.buildingType) {
        return;
      }

      window.onbeforeunload = () => true;
    };
    alertRouting();
    return () => {
      window.onbeforeunload = null;
    };
  }, [step, hookForm.getValues, hookForm]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ behavior: 'smooth', top: 0 });
    }
  }, [step]);

  const handleOpenBottomSheet = useCallback(() => {
    setIsOpenWorkableBottomSheet(true);
  }, []);

  const handleCloseBottomSheet = useCallback(() => {
    setIsOpenWorkableBottomSheet(false);
  }, []);

  const stepTitle = useCallback(
    (currentStep: Step) => {
      const data =
        consultType === 'MATCH' ? MATCH_CONSULT_DATA : SEQ_CONSULT_DATA;
      const title = data[currentStep as keyof typeof data]?.title ?? '';

      const isFirstQuestion =
        consultType === 'MATCH'
          ? currentStep === 'baseConstructionPlan.age'
          : currentStep === 'site.buildingType';

      if (isFirstQuestion && title) {
        return `반갑습니다. 고객님!\n${title}`;
      }
      return title;
    },
    [consultType],
  );

  return {
    hookForm,
    step,
    stepTitle,
    consultType,
    currentStepIndex,
    totalStepLength,
    handleChangeStep,
    handlePrev,
    handleNext,
    handleSubmit,
    userBasicInfo,
    defaultRadio,
    handleChangeIsDefaultAddress,
    stepBannerSrc,
    isTelCertificated,
    handleChangeTelCertificated,
    workAvailableAreas: expertData?.workAvailableAreas,
    agentEmployeeNo,
    isOpenWorkableBottomSheet,
    handleOpenBottomSheet,
    handleCloseBottomSheet,
  };
};

export default useSeqConsult;
